import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { Switch } from '@/components/ui/switch'
import { Input } from '@/components/ui/input'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { useToast } from '@/components/ui/use-toast'
import { EstateForSalePageFragment, useUpdateEstateRoundMutation } from '@gql'
import {
  useEstateSettingRoundOptionsSchema,
  EstateSettingRoundOptionsFormValues,
} from '@/estate/hooks/useEstateSettingRoundOptionsSchema'
import AutoSave from '@/common/atoms/Autosave'

type EstateSettingRoundOptionsProps = {
  round: EstateForSalePageFragment['estateRounds'][number]
  readOnly?: boolean
}

export const EstateSettingRoundOptions = ({
  round,
  readOnly,
}: EstateSettingRoundOptionsProps) => {
  const { t } = useTranslation()
  const { toast } = useToast()

  const { schema } = useEstateSettingRoundOptionsSchema()

  const [updateEstateRound] = useUpdateEstateRoundMutation({
    refetchQueries: ['getEstates'],
  })

  const defaultValues = useMemo(
    () => ({
      opt_blindRound: round.opt_blindRound ?? false,
      opt_enforceOfferGap: round.opt_enforceOfferGap ?? false,
      opt_minOfferGap: round.opt_minOfferGap ?? undefined,
    }),
    [round]
  )

  const form = useForm<EstateSettingRoundOptionsFormValues>({
    resolver: yupResolver(schema),
    defaultValues,
  })

  const { control, watch, setValue } = form

  const handleBlindRoundChange = (value: boolean) => {
    if (value && watch('opt_enforceOfferGap')) {
      // Désactiver enforceOfferGap si on active blindRound
      setValue('opt_enforceOfferGap', false)
    }
    setValue('opt_blindRound', value, { shouldDirty: true })
  }

  const handleEnforceOfferGapChange = (value: boolean) => {
    if (value && watch('opt_blindRound')) {
      // Désactiver blindRound si on active enforceOfferGap
      setValue('opt_blindRound', false)
    }
    setValue('opt_enforceOfferGap', value, { shouldDirty: true })
  }

  const onSubmit = form.handleSubmit(async (values) => {
    await updateEstateRound({
      variables: {
        id: round.id,
        values: {
          opt_blindRound: values.opt_blindRound,
          opt_enforceOfferGap: values.opt_enforceOfferGap,
          opt_minOfferGap: values.opt_enforceOfferGap
            ? values.opt_minOfferGap
            : null,
        },
      },
      onCompleted: () => {
        toast({
          variant: 'success',
          description: t('EstateSettingRoundOptions.toast.success'),
        })
      },
      onError: (error) => {
        console.error(error)
        toast({
          variant: 'destructive',
          title: t('EstateSettingRoundOptions.toast.error.title'),
          description: t('EstateSettingRoundOptions.toast.error.description'),
        })
      },
    })
  })

  // Reset form when round changes (e.g., when a new round is created)
  useEffect(
    () => {
      form.reset(defaultValues)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form, form.reset, round.id]
  )

  // Watch opt_enforceOfferGap for conditional rendering
  const enforceOfferGap = watch('opt_enforceOfferGap')

  return (
    <FormProvider {...form}>
      {!readOnly && <AutoSave delay={200} onSubmit={onSubmit} />}
      <form className="tw-space-y-4">
        <FormField
          control={control}
          name="opt_blindRound"
          render={({ field }) => (
            <FormItem>
              <div className="tw-flex tw-items-center tw-space-x-2">
                <FormControl>
                  <Switch
                    id="blind-round"
                    disabled={readOnly}
                    checked={field.value}
                    onCheckedChange={handleBlindRoundChange}
                  />
                </FormControl>
                <FormLabel
                  htmlFor="blind-round"
                  className="tw-text-sm tw-font-medium tw-leading-none peer-disabled:tw-cursor-not-allowed peer-disabled:tw-opacity-70"
                >
                  {t('EstateSettingRoundOptions.blindRound')}
                </FormLabel>
              </div>
              {field.value && (
                <p className="tw-text-sm tw-text-muted-foreground tw-ml-6">
                  {t('EstateSettingRoundOptions.blindRoundDescription')}
                </p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="opt_enforceOfferGap"
          render={({ field }) => (
            <FormItem>
              <div className="tw-space-y-2">
                <div className="tw-flex tw-items-center tw-space-x-2">
                  <FormControl>
                    <Switch
                      id="enforce-offer-gap"
                      disabled={readOnly}
                      checked={field.value}
                      onCheckedChange={handleEnforceOfferGapChange}
                    />
                  </FormControl>
                  <FormLabel
                    htmlFor="enforce-offer-gap"
                    className="tw-text-sm tw-font-medium tw-leading-none peer-disabled:tw-cursor-not-allowed peer-disabled:tw-opacity-70"
                  >
                    {t('EstateSettingRoundOptions.enforceOfferGap')}
                  </FormLabel>
                </div>
                {field.value && (
                  <p className="tw-text-sm tw-text-muted-foreground tw-ml-6">
                    {t('EstateSettingRoundOptions.enforceOfferGapDescription')}
                  </p>
                )}
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        {enforceOfferGap && (
          <FormField
            control={control}
            name="opt_minOfferGap"
            render={({ field }) => (
              <FormItem className="tw-ml-6">
                <div className="tw-flex tw-items-center tw-space-x-2">
                  <FormLabel
                    htmlFor="min-offer-gap"
                    className="tw-text-sm tw-font-medium"
                  >
                    {t('EstateSettingRoundOptions.minOfferGap')}
                  </FormLabel>
                  <FormControl>
                    <Input
                      id="min-offer-gap"
                      type="number"
                      min="0"
                      step="1000"
                      value={field.value}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                      disabled={readOnly}
                      className="tw-w-32"
                    />
                  </FormControl>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
      </form>
    </FormProvider>
  )
}
